import maplibregl from 'maplibre-gl';
import type { Entity, LayerId } from '../types/entity';
import { resolveEntityColor } from '../styles/tokens';

/**
 * Gerencia o ciclo de vida dos markers de "ping" no mapa. Uma instância
 * por camada, chamada de novo a cada refresh da fonte com o array
 * completo de entidades atuais — o diff (criar/atualizar/remover) é
 * feito aqui, então as fontes (`sources/*.ts`) não precisam saber nada
 * sobre o estado do mapa.
 */
export class EntityRenderer {
  private readonly map: maplibregl.Map;
  private readonly layer: LayerId;
  private readonly markers = new Map<string, maplibregl.Marker>();
  private readonly onEntityClick: (entity: Entity) => void;
  private readonly usesPingAnimation: boolean;

  /**
   * `usesPingAnimation = false` renderiza um ponto estático (classe
   * `entity-marker-static`, sem a animação de pulso) em vez do ping de
   * assinatura visual. Usado pela camada `news`: o ping comunica "dado
   * ao vivo", e a APITube no free tier tem 12h de atraso — usar o
   * mesmo ping passaria uma falsa sensação de tempo real. Todas as
   * outras camadas mantêm o comportamento padrão (default `true`).
   */
  constructor(
    map: maplibregl.Map,
    layer: LayerId,
    onEntityClick: (entity: Entity) => void,
    usesPingAnimation = true,
  ) {
    this.map = map;
    this.layer = layer;
    this.onEntityClick = onEntityClick;
    this.usesPingAnimation = usesPingAnimation;
  }

  /** Recebe o snapshot completo e atual de entidades desta camada. */
  render(entities: Entity[]): void {
    const seenIds = new Set<string>();

    for (const entity of entities) {
      seenIds.add(entity.id);
      const existing = this.markers.get(entity.id);

      if (existing) {
        existing.setLngLat([entity.lon, entity.lat]);
        continue;
      }

      const marker = this.createMarker(entity);
      marker.addTo(this.map);
      this.markers.set(entity.id, marker);
    }

    // Remove markers de entidades que saíram do snapshot (ex: voo que
    // saiu da área, sismo antigo expirando da janela de tempo).
    for (const [id, marker] of this.markers) {
      if (!seenIds.has(id)) {
        marker.remove();
        this.markers.delete(id);
      }
    }
  }

  clear(): void {
    for (const marker of this.markers.values()) marker.remove();
    this.markers.clear();
  }

  private createMarker(entity: Entity): maplibregl.Marker {
    const el = document.createElement('div');
    el.className = this.usesPingAnimation ? 'entity-ping' : 'entity-marker-static';
    el.style.setProperty('--ping-color', resolveEntityColor(this.layer, entity.severity));
    el.setAttribute('role', 'button');
    el.setAttribute('aria-label', entity.label);
    el.addEventListener('click', (ev) => {
      ev.stopPropagation();
      this.onEntityClick(entity);
    });

    return new maplibregl.Marker({ element: el, anchor: 'center' }).setLngLat([entity.lon, entity.lat]);
  }
}
